/**
 * Extracts a user-friendly error message from API errors
 */
export function getApiErrorMessage(error: unknown): string {
  if (!error) return 'An unknown error occurred';

  // Handle objects with response data (axios-like errors)
  if (typeof error === 'object' && error !== null) {
    const err = error as {
      response?: { data?: { detail?: unknown } };
      data?: { detail?: unknown };
      detail?: unknown;
      error?: { detail?: unknown };
      message?: string;
    };

    // Check for response.data.detail (common in axios/fetch wrappers)
    if (err.response?.data?.detail) {
      return formatErrorDetail(err.response.data.detail);
    }

    // Check for error.data.detail
    if (err.data?.detail) {
      return formatErrorDetail(err.data.detail);
    }

    // Check for direct detail property
    if (err.detail) {
      return formatErrorDetail(err.detail);
    }

    // Check for error.error property (some API wrappers use this)
    if (err.error?.detail) {
      return formatErrorDetail(err.error.detail);
    }

    // Fallback to message property
    if (err.message) {
      // Try to parse JSON from error message
      if (typeof err.message === 'string') {
        try {
          const parsed: unknown = JSON.parse(err.message);
          if (parsed !== null && typeof parsed === 'object' && 'detail' in parsed) {
            return formatErrorDetail((parsed as { detail?: unknown }).detail);
          }
        } catch {
          // Not JSON, use as-is
        }
      }
      return String(err.message);
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unknown error occurred';
}

/**
 * Formats the error detail object into a user-friendly message
 */
function formatErrorDetail(detail: unknown): string {
  // If detail is a string, return it directly
  if (typeof detail === 'string') {
    return detail;
  }

  // If detail is an object with message property
  if (typeof detail === 'object' && detail !== null) {
    const detailObj = detail as {
      message?: string;
      limit?: number;
      current?: number;
      plan?: string;
    };

    // Handle limit-related errors with rich context
    if (detailObj.message && detailObj.limit !== undefined) {
      const { message, limit, current, plan } = detailObj;
      return `${message}. You have ${current} of ${limit} allowed on the ${plan} plan.`;
    }

    // Handle generic message property
    if (detailObj.message) {
      return String(detailObj.message);
    }
  }

  return 'An error occurred';
}

interface ClerkError {
  message?: string;
  longMessage?: string;
  code?: string;
}

interface ClerkErrorResponse {
  errors?: ClerkError[];
}

/**
 * Extract user-friendly error message from Clerk API response
 *
 * @param error - The error object from Clerk API
 * @param defaultMessage - Message to return if no error details found
 * @returns User-friendly error message
 */
export function extractClerkError(
  error: unknown,
  defaultMessage = 'An error occurred. Please try again.'
): string {
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  const clerkError = error as ClerkErrorResponse;
  const firstError = clerkError.errors?.[0];

  if (firstError?.message) {
    return firstError.message;
  }

  if (firstError?.longMessage) {
    return firstError.longMessage;
  }

  return defaultMessage;
}

/**
 * Extract error message from mutation error with rate limit handling.
 *
 * Per rate_limiting.py:178-189, 429 responses have structure:
 * {
 *   detail: {
 *     message: "Rate limit exceeded",
 *     error: "too_many_requests",
 *     retry_after_seconds: "60",
 *     quota_info: "..."
 *   }
 * }
 *
 * Also checks Retry-After header as fallback.
 * For non-429 errors, delegates to getApiErrorMessage (§BASIC.2.1 - DRY).
 *
 * @param error - The error object from API mutation
 * @param defaultMessage - Message to return if extraction fails
 * @returns User-friendly error message
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage = 'An unknown error occurred'
): string {
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  const err = error as {
    response?: {
      status?: number;
      data?: {
        detail?:
          | string
          | {
              message?: string;
              retry_after_seconds?: string | number;
            };
      };
      headers?: Record<string, string>;
    };
    message?: string;
  };

  // Handle 429 rate limit response (§BE.8.1 - rate limiting)
  if (err.response?.status === 429) {
    const detail = err.response.data?.detail;

    // Extract retry_after_seconds from detail object
    if (typeof detail === 'object' && detail !== null) {
      const retryAfter = detail.retry_after_seconds;
      if (retryAfter !== undefined && retryAfter !== null) {
        const seconds =
          typeof retryAfter === 'string' ? parseInt(retryAfter, 10) : Number(retryAfter);
        if (!isNaN(seconds)) {
          return `Rate limit exceeded. Try again in ${seconds} seconds.`;
        }
      }
    }

    // Fallback to Retry-After header
    const retryAfterHeader = err.response.headers?.['retry-after'];
    if (retryAfterHeader) {
      const seconds = parseInt(retryAfterHeader, 10);
      if (!isNaN(seconds)) {
        return `Rate limit exceeded. Try again in ${seconds} seconds.`;
      }
    }

    return 'Rate limit exceeded. Please try again later.';
  }

  // Delegate non-429 errors to existing error handler (§BASIC.2.1 - DRY)
  return getApiErrorMessage(error);
}
